import { motion } from "framer-motion";
import { Icon } from "@iconify/react";
import type { Catalogo } from "@/types/planos";
import { linkDeRegisto, queryDoTipo } from "@/Logigate/components/planos/selecao";
import TipoEmpresa from "@/utils/logigate/TipoEmpresa";
import { formataDinheiro } from "@/utils/ForAll";
import { CartaoComboDoSite } from "./planosDoSite";

interface Props {
    /** O catálogo da conta de prestador (`CatalogoDePlanos::paraApp` com o contexto de prestador): só combos. */
    catalogo: Catalogo;
    /** Para onde vai "Veja os planos para empresas": uma âncora na página inicial, ou `/#precos` noutra página. */
    hrefEmpresas?: string;
    /** Com um link, mostra "Saber mais" para a página dedicada aos prestadores. */
    hrefSaberMais?: string;
}

function irParaRegisto(query: Record<string, string>) {
    window.location.href = linkDeRegisto(query);
}

function irPara(href: string) {
    if (!href.startsWith("#")) {
        window.location.href = href;

        return;
    }

    document.querySelector(href)?.scrollIntoView({ behavior: "smooth", block: "start" });
}

/**
 * Os combos da conta de prestador de serviços, logo abaixo dos planos das
 * empresas (e a abrir a página dedicada). Cada cartão leva ao registo já como
 * prestador e com o combo escolhido; os limites mostram-se todos, porque são
 * eles que vendem o plano.
 */
export default function PrestadoresSection({ catalogo, hrefEmpresas = "#precos", hrefSaberMais }: Props) {
    const combos = catalogo.combos;

    if (combos.length === 0) {
        return null;
    }

    const minimo = Math.min(...combos.map((combo) => combo.preco_final));

    return (
        <section id="prestadores" className="py-20 lg:py-28 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="text-center mb-12">
                    <div className="flex items-center justify-center gap-2 mb-4">
                        <div className="h-px w-8 bg-blue-500" />
                        <span className="text-blue-600 text-sm font-medium tracking-wider uppercase">Para quem trabalha por conta própria</span>
                        <div className="h-px w-8 bg-blue-500" />
                    </div>
                    <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Logigate para prestadores de serviços</h2>
                    <p className="text-lg text-gray-500 max-w-2xl mx-auto">
                        Emites facturas de serviços em teu nome? Há um Logigate mais simples e mais barato para ti: facturação certificada pela AGT e controlo do dinheiro que entra e sai, a partir de{" "}
                        {formataDinheiro(minimo)}/mês, com IVA incluído.
                    </p>
                    {hrefSaberMais && (
                        <a href={hrefSaberMais} className="inline-flex items-center gap-1.5 mt-4 text-sm font-medium text-blue-600 hover:text-blue-700">
                            Saber mais sobre a conta de prestador
                            <Icon icon="mdi:arrow-right" width={16} />
                        </a>
                    )}
                </motion.div>

                <div className={`grid gap-6 ${combos.length === 1 ? "max-w-md mx-auto" : combos.length === 2 ? "max-w-3xl mx-auto md:grid-cols-2" : "md:grid-cols-2 lg:grid-cols-3"}`}>
                    {combos.map((combo, indice) => (
                        <motion.div key={combo.id} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: indice * 0.1 }}>
                            <CartaoComboDoSite
                                combo={combo}
                                rotulo="Começar como prestador"
                                limitesVisiveis={99}
                                destaque={combos.length > 2 ? indice === 1 : indice === 0}
                                onEscolher={() => irParaRegisto({ ...queryDoTipo(TipoEmpresa.PRESTADOR), combo: String(combo.id), meses: "1" })}
                            />
                        </motion.div>
                    ))}
                </div>

                <p className="text-center text-sm text-gray-400 mt-8">
                    Preços mensais com IVA{catalogo.iva.taxa > 0 ? ` (${catalogo.iva.taxa}%)` : ""} incluído. Os planos de prestador não têm módulos à parte: escolhe um e fica pronto a facturar.
                </p>
                <p className="text-center text-sm text-gray-500 mt-2">
                    Tem produtos, armazém ou várias lojas?{" "}
                    <button type="button" onClick={() => irPara(hrefEmpresas)} className="text-blue-600 font-medium hover:underline">
                        Vê os planos para empresas
                    </button>
                    .
                </p>
            </div>
        </section>
    );
}
